/**
 * Small on-brand UI primitives shared by the auth/admin/account screens: Button, TextField, FormError,
 * AuthShell (centered branded container), plus a friendly mapper for API error codes.
 */
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";
import { ApiError } from "../api/client";
import { cx } from "../lib/ui";

export function Button({
  variant = "primary",
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "secondary" | "ghost" }) {
  return (
    <button
      className={cx(
        "inline-flex items-center justify-center gap-2 rounded-sm px-5 py-2.5 font-medium transition-colors focus-visible:shadow-[var(--shadow-focus)] disabled:cursor-not-allowed disabled:opacity-40",
        variant === "primary" && "bg-brand text-white hover:bg-brand-hover",
        variant === "secondary" && "border border-ink-300 text-ink hover:border-ink",
        variant === "ghost" && "text-ink hover:bg-ink-100",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function TextField({
  label,
  error,
  id,
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string }) {
  const inputId = id ?? props.name;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="text-sm font-medium text-ink-700">
        {label}
      </label>
      <input
        id={inputId}
        className={cx(
          "rounded-sm border bg-white px-3 py-2 text-ink outline-none focus-visible:shadow-[var(--shadow-focus)]",
          error ? "border-[var(--danger)]" : "border-ink-300",
          className,
        )}
        {...props}
      />
      {error && <p className="text-sm text-[var(--danger)]">{error}</p>}
    </div>
  );
}

export function FormError({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-sm border border-[var(--danger)] bg-[var(--danger-bg)] px-3 py-2 text-sm text-[var(--danger)]">
      {children}
    </p>
  );
}

export function AuthShell({ children }: { children: ReactNode }) {
  return (
    <main className="grid min-h-svh place-items-center bg-paper px-5 py-10">
      <div className="w-full max-w-md">{children}</div>
    </main>
  );
}

/** Map an API error to a player-friendly message. */
export function apiErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case "invalid_credentials":
        return "Those details don't match. Please try again.";
      case "invalid_or_expired_link":
        return "This access link has expired. Ask your administrator for a new one.";
      case "email_taken":
        return "An account with that email already exists — try signing in instead.";
      case "invalid_request":
        return "Please check the form and try again.";
      case "forbidden":
        return "You don't have permission to do that.";
      case "access_revoked":
        return "Your access link was revoked or has expired. Ask your administrator for a new link.";
      default:
        return err.message || "Something went wrong. Please try again.";
    }
  }
  return "Something went wrong. Please try again.";
}
